import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertVariants, AlertAppearances, AlertOrientations } from '@carboledger/ui';
import { LoadingOverlay } from '@/components/feedback';
import { ROUTES } from '@/app/routes';
import { AuthLayout } from '../components/AuthLayout';
import { ResetPasswordForm } from '../components/ResetPasswordForm';
import { useVerifyResetTokenQuery, useResetPasswordMutation } from '../api/authApi';
import styles from './ResetPasswordPage.module.scss';
import { useTranslations } from '@/hooks';

// Reached via the link iscc-backend's forgotPassword controller emails.
export const ResetPasswordPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();

  const handleBackToLogin = useCallback(() => {
    navigate(ROUTES.login);
  }, [navigate]);
  const { token } = useParams();

  const { isLoading: isVerifying, error: verifyError } = useVerifyResetTokenQuery(token);
  const [resetPassword, { isLoading: isSubmitting, error: submitError }] =
    useResetPasswordMutation();

  const handleSubmit = async ({ password }) => {
    const result = await resetPassword({ token, password });
    if (result.error) return;
    navigate(ROUTES.login);
  };

  if (isVerifying) {
    return (
      <AuthLayout>
        <LoadingOverlay label={t('AUTH_CHECKING_RESET_LINK')} />
      </AuthLayout>
    );
  }

  if (verifyError) {
    return (
      <AuthLayout>
        <Alert
          variant={AlertVariants.danger}
          appearance={AlertAppearances.accent}
          orientation={AlertOrientations.horizontal}
          className={styles.alert}
        >
          {verifyError.data?.message ?? t('AUTH_RESET_LINK_INVALID')}
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <ResetPasswordForm
        onSubmit={handleSubmit}
        onBackToLogin={handleBackToLogin}
        isLoading={isSubmitting}
        error={submitError}
      />
    </AuthLayout>
  );
};
